import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { createBirthdayEvent } from '@/lib/eventUtils';

// Use exact same types as useEvents.tsx to maintain compatibility
export interface FamilyEvent {
  id: string;
  family_id: string;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  created_by: string;
  event_type: 'personal' | 'family' | 'appointment' | 'birthday' | 'holiday';
  location?: string;
  latitude?: number;
  longitude?: number;
  is_recurring: boolean;
  recurrence_pattern?: string;
  linked_persons?: string[];
  created_at: string;
  updated_at: string;
}

export interface EventAttendee {
  id: string;
  event_id: string;
  profile_id: string;
  status: 'invited' | 'accepted' | 'declined' | 'maybe';
  created_at: string;
  profiles?: {
    first_name: string;
    last_name: string;
  };
}

const CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const STALE_TIME = 60 * 1000; // 1 minute for faster updates

export function useOptimizedEvents(familyId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query for events with optimized caching
  const eventsQuery = useQuery({
    queryKey: ['events', familyId],
    queryFn: async (): Promise<FamilyEvent[]> => {
      if (!familyId) return [];
      
      const { data, error } = await supabase
        .from('family_events')
        .select('*')
        .eq('family_id', familyId)
        .order('start_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!familyId,
    staleTime: 30 * 1000, // Keep data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes during navigation
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Mutation to create event with optimistic updates
  const createEventMutation = useMutation({
    mutationFn: async ({ 
      eventData, 
      attendeeIds = [],
      silent = false
    }: { 
      eventData: Omit<FamilyEvent, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'family_id'>;
      attendeeIds?: string[];
      silent?: boolean;
    }) => {
      if (!familyId || !user?.id) {
        throw new Error('Missing familyId or user');
      }

      // Get current user profile ID - same logic as useEvents.tsx
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;

      // Validate dates - same logic as useEvents.tsx
      const startDate = new Date(eventData.start_date);
      const endDate = new Date(eventData.end_date);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Invalid dates');
      }
      
      if (endDate <= startDate) {
        throw new Error('End date must be after start date');
      }

      const insertData = {
        ...eventData,
        family_id: familyId,
        created_by: profile.id,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      };

      const { data: eventResult, error: eventError } = await supabase
        .from('family_events')
        .insert(insertData)
        .select()
        .single();

      if (eventError) throw eventError;

      // Add attendees if specified - same logic as useEvents.tsx
      if (attendeeIds.length > 0) {
        // Clean the attendee IDs to remove any prefixes
        const cleanedAttendeeIds = attendeeIds.map(cleanAttendeeId);
        const attendeeData = cleanedAttendeeIds.map(profileId => ({
          event_id: eventResult.id,
          profile_id: profileId,
          status: 'invited' as const
        }));

        const { error: attendeeError } = await supabase
          .from('family_event_attendees')
          .insert(attendeeData);

        if (attendeeError) {
          console.error('Attendee error:', attendeeError);
        }
      }

      return { ...eventResult, silent };
    },
    onMutate: async ({ eventData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['events', familyId] });

      // Snapshot previous value
      const previousEvents = queryClient.getQueryData<FamilyEvent[]>(['events', familyId]);

      // Optimistically update to the new value
      if (previousEvents && user) {
        const optimisticEvent: FamilyEvent = {
          id: `temp-${Date.now()}`,
          ...eventData,
          family_id: familyId!,
          created_by: 'temp',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        queryClient.setQueryData<FamilyEvent[]>(['events', familyId], [
          ...previousEvents,
          optimisticEvent
        ]);
      }

      return { previousEvents };
    },
    onError: (err, variables, context) => {
      // If mutation fails, use the context returned from onMutate to roll back
      if (context?.previousEvents) {
        queryClient.setQueryData(['events', familyId], context.previousEvents);
      }

      toast({
        title: "Erreur",
        description: "Impossible de créer l'événement",
        variant: "destructive"
      });
    },
    onSuccess: (data) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['events', familyId] });
      
      // Only show toast if not silent
      if (!data.silent) {
        toast({
          title: "Événement créé",
          description: "L'événement a été ajouté au calendrier"
        });
      }
    },
  });

  // Helper function to clean attendee IDs by removing prefixes
  const cleanAttendeeId = (id: string): string => {
    // Remove prefixes like "pp_", "profile:", "person:" etc.
    if (id.startsWith('pp_')) {
      return id.substring(3);
    }
    if (id.startsWith('profile:')) {
      return id.substring(8);
    }
    if (id.startsWith('person:')) {
      return id.substring(7);
    }
    return id;
  };

  // Mutation to update event with attendees
  const updateEventMutation = useMutation({
    mutationFn: async ({ 
      eventId, 
      eventData, 
      attendeeIds 
    }: { 
      eventId: string; 
      eventData: Partial<FamilyEvent>; 
      attendeeIds?: string[] 
    }) => {
      console.log('🔄 updateEventMutation started:', { eventId, eventData, attendeeIds });
      
      // Update event data
      const { error: eventError } = await supabase
        .from('family_events')
        .update(eventData)
        .eq('id', eventId);

      if (eventError) {
        console.error('❌ Event update error:', eventError);
        throw eventError;
      }
      
      // Update attendees if provided
      if (attendeeIds !== undefined) {
        // Clean the attendee IDs to remove any prefixes
        const cleanedAttendeeIds = attendeeIds.map(cleanAttendeeId);
        console.log('🔄 Updating attendees...', { eventId, originalIds: attendeeIds, cleanedIds: cleanedAttendeeIds });
        
        // Remove all existing attendees
        const { error: deleteError } = await supabase
          .from('family_event_attendees')
          .delete()
          .eq('event_id', eventId);

        if (deleteError) {
          console.error('❌ Error removing old attendees:', deleteError);
          throw deleteError;
        }

        // Add new attendees if any
        if (cleanedAttendeeIds.length > 0) {
          const attendeeData = cleanedAttendeeIds.map(profileId => ({
            event_id: eventId,
            profile_id: profileId,
            status: 'invited' as const
          }));

          const { error: insertError } = await supabase
            .from('family_event_attendees')
            .insert(attendeeData);

          if (insertError) {
            console.error('❌ Error inserting new attendees:', insertError);
            throw insertError;
          }
          
          console.log('✅ Attendees updated successfully:', attendeeData);
        } else {
          console.log('✅ All attendees removed successfully');
        }
      }
      
      console.log('✅ Event and attendees updated successfully');
    },
    onMutate: async ({ eventId, eventData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['events', familyId] });
      
      // Snapshot previous value
      const previousEvents = queryClient.getQueryData<FamilyEvent[]>(['events', familyId]);
      
      // Optimistically update the event
      if (previousEvents) {
        const updatedEvents = previousEvents.map(event => 
          event.id === eventId 
            ? { ...event, ...eventData, updated_at: new Date().toISOString() }
            : event
        );
        queryClient.setQueryData<FamilyEvent[]>(['events', familyId], updatedEvents);
      }
      
      return { previousEvents };
    },
    onError: (err, variables, context) => {
      // Roll back on error
      if (context?.previousEvents) {
        queryClient.setQueryData(['events', familyId], context.previousEvents);
      }
      
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'événement",
        variant: "destructive"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', familyId] });
      
      toast({
        title: "Événement modifié",
        description: "L'événement a été mis à jour"
      });
    },
  });

  // Mutation to delete event
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from('family_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
    },
    onMutate: async (eventId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['events', familyId] });
      
      // Snapshot previous value
      const previousEvents = queryClient.getQueryData<FamilyEvent[]>(['events', familyId]);
      
      // Optimistically remove the event
      if (previousEvents) {
        const filteredEvents = previousEvents.filter(event => event.id !== eventId);
        queryClient.setQueryData<FamilyEvent[]>(['events', familyId], filteredEvents);
      }
      
      return { previousEvents };
    },
    onError: (err, eventId, context) => {
      // Roll back on error
      if (context?.previousEvents) {
        queryClient.setQueryData(['events', familyId], context.previousEvents);
      }
      
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'événement",
        variant: "destructive"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', familyId] });
      
      toast({
        title: "Événement supprimé",
        description: "L'événement a été retiré du calendrier"
      });
    },
  });

  // Create birthday events for year mutation - now uses the safe idempotent function
  const createBirthdayEventsMutation = useMutation({
    mutationFn: async (year?: number) => {
      if (!familyId) throw new Error('Family ID required');
      
      const targetYear = year || new Date().getFullYear();
      
      // Use the safe database function that prevents duplicates
      const { data, error } = await supabase.rpc('create_birthday_events_for_year', {
        _family_id: familyId,
        _year: targetYear
      });
      
      if (error) {
        console.error('Error creating birthday events:', error);
        throw error;
      }
      
      const result = data as { events_created: number; events_skipped: number; year: number };
      
      if (result.events_created > 0) {
        toast({
          title: "Événements d'anniversaire créés",
          description: `${result.events_created} événement(s) d'anniversaire créé(s) pour ${result.year}.`,
        });
      } else if (result.events_skipped > 0) {
        toast({
          title: "Anniversaires déjà créés",
          description: `Tous les anniversaires pour ${result.year} existent déjà.`,
        });
      } else {
        toast({
          title: "Aucune date de naissance",
          description: "Aucune personne avec une date de naissance trouvée.",
        });
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', familyId] });
    },
    onError: (error) => {
      console.error('Birthday events creation failed:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer les événements d'anniversaire.",
        variant: "destructive",
      });
    },
  });

  // Helper function to get upcoming events
  const getUpcomingEvents = (limit = 5) => {
    const now = new Date();
    return (eventsQuery.data || [])
      .filter(event => new Date(event.start_date) >= now)
      .slice(0, limit);
  };

  // Function to update attendee status
  const updateAttendeeStatus = async (eventId: string, profileId: string, status: 'accepted' | 'declined' | 'maybe') => {
    try {
      const { error } = await supabase
        .from('family_event_attendees')
        .upsert({
          event_id: eventId,
          profile_id: profileId,
          status
        });

      if (error) throw error;

      toast({
        title: "Statut mis à jour",
        description: `Votre réponse a été enregistrée`
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour votre réponse",
        variant: "destructive"
      });
    }
  };

  // Function to delete related recurring events
  const deleteRelatedRecurringEvents = async (originalEvent: FamilyEvent) => {
    const thresholdIso = new Date(
      Math.min(new Date().getTime(), new Date(originalEvent.start_date).getTime())
    ).toISOString();

    // Récupérer un ensemble large puis filtrer côté client pour fiabilité
    const { data: candidates, error } = await supabase
      .from('family_events')
      .select('id, title, event_type, recurrence_pattern, start_date, created_by')
      .eq('family_id', familyId)
      .eq('is_recurring', true)
      .eq('event_type', originalEvent.event_type)
      .eq('created_by', originalEvent.created_by)
      .gte('start_date', thresholdIso)
      .neq('id', originalEvent.id);

    if (error) throw error;

    const eventIds = (candidates || [])
      .filter(e =>
        // même pattern qu'avant OU même titre (pour couvrir les anciens événements sans pattern)
        (originalEvent.recurrence_pattern ? e.recurrence_pattern === originalEvent.recurrence_pattern : true)
        || e.title === originalEvent.title
      )
      .map(e => e.id);

    if (eventIds.length > 0) {
      await supabase
        .from('family_events')
        .delete()
        .in('id', eventIds);
      console.log(`🗑️ Deleted ${eventIds.length} related recurring events`);
      await eventsQuery.refetch();
    } else {
      console.log('ℹ️ No related recurring events found to delete');
    }
  };

  // Function to recreate recurring events with new pattern
  const recreateRecurringEvents = async (baseEvent: FamilyEvent, newPattern: string) => {
    if (newPattern === 'none') return;

    const { generateRecurringEvents } = await import('../lib/recurrenceUtils');
    
    const recurringEventData = {
      title: baseEvent.title,
      description: baseEvent.description,
      start_date: baseEvent.start_date,
      end_date: baseEvent.end_date,
      event_type: baseEvent.event_type,
      location: baseEvent.location,
      is_recurring: true,
      recurrence_pattern: newPattern,
      linked_persons: baseEvent.linked_persons || []
    };

    const newEvents = generateRecurringEvents(recurringEventData, newPattern as any, 52); // Generate 1 year
    
    // Create new events (skip the first one as it's the original event)
    for (const eventData of newEvents.slice(1)) {
      await createEventMutation.mutateAsync({ 
        eventData: {
          ...eventData,
          family_id: familyId!,
          created_by: baseEvent.created_by
        } as any, 
        attendeeIds: [], 
        silent: true 
      });
    }
    
    console.log(`✨ Created ${newEvents.length - 1} new recurring events`);
    await eventsQuery.refetch();
  };
    

    return {
    events: eventsQuery.data || [],
    loading: eventsQuery.isLoading,
    isError: eventsQuery.isError,
    error: eventsQuery.error,
    createEvent: (eventData: Omit<FamilyEvent, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'family_id'>, attendeeIds: string[] = [], silent: boolean = false) => {
      return createEventMutation.mutateAsync({ eventData, attendeeIds, silent });
    },
    updateEvent: (eventId: string, eventData: Partial<FamilyEvent>, attendeeIds?: string[]) => {
      return updateEventMutation.mutateAsync({ eventId, eventData, attendeeIds });
    },
    deleteEvent: deleteEventMutation.mutate,
    deleteRelatedRecurringEvents,
    recreateRecurringEvents,
    createBirthdayEventsForYear: createBirthdayEventsMutation.mutate,
    getUpcomingEvents,
    updateAttendeeStatus,
    isCreating: createEventMutation.isPending,
    isUpdating: updateEventMutation.isPending,
    isDeleting: deleteEventMutation.isPending,
    refreshEvents: () => eventsQuery.refetch(),
  };
}